import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Dimensions,
  ActivityIndicator,
  Modal
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import * as FileSystem from 'expo-file-system';
import Tesseract from 'tesseract.js';
import { API_BASE_URL } from '../config/api';
import Constants from "expo-constants";
// Note: Registration endpoint not documented in current API specification
// import { registerUser } from '../services/authApi';
import { useFocusEffect } from '@react-navigation/native';
import '../styles/custom.css';
import { useFonts } from 'expo-font';
import { HeroOutline } from '@nandorojo/heroicons';
import { MaskedTextInput } from "react-native-mask-text";
import { doLogout } from '../services/userDataService';
import { useCustomAlertModal } from '../components/CustomAlertModal';
import { useBannerWithPopUp } from '../components/BannerWithPopUp';

const { width, height } = Dimensions.get('window');

export default function PhoneFormScreen({ navigation }) {
  const { 
    customAlertModal, 
    showCustomAlert, 
    hideCustomAlert, 
    handleCustomAlertConfirm
  } = useCustomAlertModal();
  const { 
    bannerWrapper
  } = useBannerWithPopUp( { marginTop: 16 } );

  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = () => {
    console.log('=== FORM VALIDATION START ===');

    // Validate that phone is provided
    if (!phone.trim()) {
      console.log('VALIDATION FAILED: No phone provided');
      showCustomAlert('Error', 'Nomor handphone harus diisi');
      return false;
    }

    // Validate phone format if phone is provided
    if (phone.trim()) {
      // Basic phone validation - should contain only numbers and common phone characters
      const phoneRegex = /^[\d\s\-\+\(\)]+$/;
      if (!phoneRegex.test(phone)) {
        console.log('VALIDATION FAILED: Invalid phone format:', phone);
        showCustomAlert('Error', 'Format nomor handphone tidak valid');
        return false;
      }
    }

    // Validate phone format to make sure it is valid Indonesian cellular number without heading of 08/628/+628
    if (phone.length < 8 || phone.length > 13) {
      console.log('VALIDATION FAILED: Invalid phone format:', phone);
      showCustomAlert('Error', 'Format nomor handphone tidak valid');
      return false;
    }

    // Validate phone format to make sure it is valid Indonesian cellular number without heading of 08/628/+628
    if (phone.startsWith('08') || phone.startsWith('628') || phone.startsWith('+628')) {
      console.log('VALIDATION FAILED: Invalid phone format:', phone);
      showCustomAlert('Error', 'Ketikkan nomor handphone setelah "08" saja');
      return false;
    }

    console.log('=== FORM VALIDATION PASSED ===');
    return true;
  };

  const handleCreatePhone = async () => {
    console.log('handleCreatePhone called');
    console.log('isLoading:', isLoading);

    if (!validateForm()) {
      console.log('Form validation failed');
      return;
    }

    console.log('Form validation passed, starting registration...');
    setIsLoading(true);

    try {
      const completePhone = `08${phone}` || "";

      // Prepare registration data according to updated API specification
      const registrationData = {
        'email': email || "",
        'telp': completePhone,
      };

      console.log('Sending registration request...');

      const response = await fetch(`${API_BASE_URL}/api/create-phone`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(registrationData)
      });

      const result = await response.json();
      // console.log('Registration response:', result);

      if (result.status === 'success' && result.data) {
        // make sure the password saved
        if( !result.data.hasPhone ) {
          setTimeout(() => {
            window.location.reload()
          }, 2000);
        } else {
          // Store user data if registration successful
          if (result.data.userData) {
            await AsyncStorage.setItem('userData', JSON.stringify(result.data.userData));

            // Store session data if provided
            if (result.data.userData.remember_token) {
              await AsyncStorage.setItem('userToken', result.data.userData.remember_token);
            }
            // Store session data if provided
            if (result.data.userData.session_id) {
              await AsyncStorage.setItem('session_id', result.data.userData.session_id);
            }
          }

          window.location.reload();
        }
      } else {
        showCustomAlert('Error', result.message || 'Pendaftaran gagal. Silakan coba lagi.');
      }

    } catch (error) {
      console.error('Registration error:', error);
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        showCustomAlert('Error', 'Koneksi internet bermasalah. Silakan periksa koneksi Anda.');
      } else {
        showCustomAlert('Error', error.message || 'Pendaftaran gagal. Silakan coba lagi.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useFocusEffect(
    React.useCallback(()=>{
      async function fetchLocalData() {
        try{
          const userDataString = await AsyncStorage.getItem('userData');
          if(!!userDataString) {
            const userData = JSON.parse(userDataString);

            if( userData?.user_email ) {
              setEmail(userData?.user_email);
            } else {
              doLogout();
            }
          }
        } catch(e) {
          console.log(e);
          doLogout();
        }
      }

      fetchLocalData();
    }, [navigation])
  );

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <View style={styles.container}>
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.header}>
            <TouchableOpacity
              style={styles.backButton}
              onPress={() => navigation.goBack()}
            >
              {/*<Ionicons name="arrow-back" size={24} color="#000000" />*/}
            </TouchableOpacity>
          </View>

          <View style={styles.content}>
            <View style={styles.titleSection}>
              <View style={styles.titleIconContainer}>
                <HeroOutline.DevicePhoneMobile height="40" width="40" color="#1E293B" />
              </View>
              <Text style={styles.title}>Masukkan Nomor Handphone</Text>
              <Text style={styles.subtitle}>Masukkan nomor handphone-mu sekali untuk melanjutkan.</Text>
            </View>

            <View style={styles.formSection}>
              <View style={styles.formGroup}>
                <Text style={styles.label}>Nomor Handphone</Text>
                <View style={styles.inputContainer}>
                  <Text style={styles.inputPrefix}>08-</Text>
                  <MaskedTextInput
                    mask="999-9999999999"
                    style={styles.input}
                    placeholder="xxx-xxxxxxx"
                    placeholderTextColor="#94A3B8"
                    value={phone}
                    onChangeText={(text, rawText) => {
                      setPhone(rawText);
                    }}
                    keyboardType="phone-pad"
                    autoCorrect={false}
                  />
                </View>
              </View>

              <TouchableOpacity
                style={[styles.registerButton, (isLoading || !phone) && styles.disabledButton]}
                onPress={handleCreatePhone}
                disabled={(isLoading || !phone)}
              >
                <Text style={styles.registerButtonText}>
                  {isLoading ? 'Memproses...' : 'Lanjutkan'}
                </Text>
              </TouchableOpacity>

              {bannerWrapper}
            </View>
          </View>
        </ScrollView>
      </View>

      {customAlertModal}
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  scrollContent: {
    flexGrow: 1,
    padding: 16,
    paddingTop: 32,
  },
  header: {
    marginBottom: 32,
  },
  backButton: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    flex: 1,
  },
  titleSection: {
    marginBottom: 32,
  },
  titleIconContainer: {
    width: 64,
    height: 64,
    marginHorizontal: 'auto',
    borderRadius: 'calc(infinity * 1px)',
    backgroundColor: '#F8FAFC',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  title: {
    width: '100%',
    fontSize: 20,
    lineHeight: 28,
    fontFamily: 'Poppins-Semibold',
    fontWeight: 600,
    color: '#00000',
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    width: '100%',
    fontSize: 14,
    lineHeight: 24,
    fontFamily: 'Poppins',
    color: '#64748B',
    textAlign: 'center',
  },
  formSection: {
    flex: 1,
  },
  formGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontFamily: 'Poppins-Medium',
    fontWeight: 500,
    color: '#1E293B',
    marginBottom: 8,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#AAAAAA',
    paddingHorizontal: 16,
    height: 48,
  },
  inputContainerDisabled: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E2E8F0',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#AAAAAA',
    paddingHorizontal: 16,
    height: 48,
  },
  input: {
    flex: 1,
    fontSize: 16,
    color: '#1E293B',
    fontFamily: 'Poppins',
  },
  inputDisabled: {
    flex: 1,
    fontSize: 16,
    color: '#1E293B',
    backgroundColor: '#E2E8F0',
  },
  inputPrefix: {
    fontSize: 16,
    color: '#1E293B',
    fontFamily: 'Poppins',
    // marginRight: 8,
  },
  eyeIcon: {
    padding: 4,
  },
  inputIcon: {
    marginLeft: 8,
  },
  uploadContainer: {
    borderWidth: 1,
    borderColor: '#94A3B8',
    borderStyle: 'dashed',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    height: 136,
    justifyContent: 'center',
    alignItems: 'center',
  },
  uploadContent: {
    alignItems: 'center',
    width: 208,
  },
  uploadText: {
    fontSize: 14,
    color: '#1E293B',
    marginTop: 8,
    fontWeight: '600',
    fontFamily: 'Poppins',
    textAlign: 'center',
  },
  uploadSubtext: {
    fontSize: 12,
    color: '#94A3B8',
    marginTop: 4,
    fontFamily: 'Poppins',
    textAlign: 'center',
    lineHeight: 16,
  },
  uploadContainerProcessing: {
    borderColor: '#3B82F6',
    backgroundColor: '#F0F9FF',
  },
  ocrHintText: {
    fontSize: 11,
    color: '#3B82F6',
    marginTop: 6,
    fontFamily: 'Poppins',
    textAlign: 'center',
    fontWeight: '500',
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 24,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
    marginTop: 2,
  },
  checkboxChecked: {
    backgroundColor: '#000000',
    borderColor: '#000000',
  },
  checkboxText: {
    flex: 1,
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
  },
  linkText: {
    color: '#3B82F6',
    fontWeight: '500',
  },
  registerButton: {
    backgroundColor: '#1E293B',
    borderRadius: 6,
    padding: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  disabledButton: {
    opacity: 0.5,
  },
  registerButtonText: {
    fontFamily: 'Poppins-Medium',
    fontWeight: 500,
    fontSize: 14,
    lineHeight: 20,
    color: '#FFFFFF',
  },
  loginContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loginText: {
    fontSize: 14,
    fontFamily: 'Poppins',
    fontWeight: 400,
    color: '#64748B',
  },
  loginLink: {
    fontSize: 14,
    fontFamily: 'Poppins-Semibold',
    fontWeight: 600,
    color: '#1E293B',
  },
});